<template>
  <div class="py-5 pl-5 pr-3 border-b border-chrome-light grid grid-cols-[1fr_auto] items-center gap-x-2 gap-y-3">
    <template v-if="activeRoleContent.buttons.edit">
      <AppRouterButton
        :to="{ name: 'app_plan_document', params: { municipality: municipalityId } }"
        class="action-btn group"
      >
        <FontAwesomeIcon
          :icon="faPencil"
          class="text-base text-black group-hover:text-white"
        />
        Edit the document in the builder
      </AppRouterButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.viewPdf">
      <AppRouterButton
        :to="{ name: 'app_plan_preview_document', params: { municipality: municipalityId } }"
        class="action-btn group"
        target="_blank"
      >
        <FontAwesomeIcon
          :icon="faEye"
          class="text-base text-black group-hover:text-white"
        />
        Preview the Document
      </AppRouterButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.notifyAdminToSubmit">
      <AppButton
        class="action-btn group"
        @click="$emit('notifyAdminToSubmit')"
      >
        <FontAwesomeIcon
          :icon="faBellOn"
          class="text-base text-black group-hover:text-white"
        />
        Notify the Municipal Administrator to submit
      </AppButton>

      <AppTooltip class="[&_.tooltip-body]:w-[165px] [&_.tooltip-body]:text-[11px] [&_.tooltip-body]:pl-[6px]">
        <template #reference>
          <FontAwesomeIcon
            :icon="faCircleInfo"
            class="text-xl text-black"
          />
        </template>
        Once you notify the municipal administrator, the document will be locked and you will no longer be able to make edits.
      </AppTooltip>
    </template>

    <template v-if="activeRoleContent.buttons.notifyAdminToApprove">
      <AppButton
        class="action-btn group"
        :disabled="releaseBlocked"
        @click="$emit('notifyAdminToApprove')"
      >
        <FontAwesomeIcon
          :icon="faBellOn"
          class="text-base text-black group-hover:text-white"
        />
        Notify the Municipal Administrator to approve
      </AppButton>

      <AppTooltip
        v-if="releaseBlocked"
        class="[&_.tooltip-body]:w-[180px] [&_.tooltip-body]:text-[11px] [&_.tooltip-body]:pl-[6px]"
      >
        <template #reference>
          <FontAwesomeIcon
            :icon="faCircleInfo"
            class="text-xl text-black"
          />
        </template>
        {{ releaseBlockedMessage }}
      </AppTooltip>
      <span v-else />
    </template>

    <template v-if="activeRoleContent.buttons.submitForFirstMec">
      <AppButton
        class="action-btn group"
        :disabled="releaseBlocked"
        @click="$emit('submitForFirstMec')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Confirm submission to MEC for review
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.submitForApproval">
      <AppButton
        class="action-btn group"
        :disabled="releaseBlocked"
        @click="$emit('submitForApproval')"
      >
        <FontAwesomeIcon
          :icon="faFilePen"
          class="text-base text-black group-hover:text-white"
        />
        Move to approval
      </AppButton>

      <AppTooltip
        v-if="releaseBlocked"
        class="[&_.tooltip-body]:w-[180px] [&_.tooltip-body]:text-[11px] [&_.tooltip-body]:pl-[6px]"
      >
        <template #reference>
          <FontAwesomeIcon
            :icon="faCircleInfo"
            class="text-xl text-black"
          />
        </template>
        {{ releaseBlockedMessage }}
      </AppTooltip>
      <span v-else />
    </template>

    <template v-if="activeRoleContent.buttons.approveDraftForCouncil">
      <AppButton
        class="action-btn group"
        @click="$emit('approveDraftForCouncil')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Approve draft for Council noting
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.approve">
      <AppButton
        class="action-btn group"
        @click="$emit('approve')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Approve draft and publish
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.rejectDraft">
      <AppButton
        class="action-btn group"
        @click="$emit('rejectDraft')"
      >
        <FontAwesomeIcon
          :icon="faXmark"
          class="text-lg text-black group-hover:text-white"
        />
        Reject draft
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.submitForFinalMec">
      <AppButton
        class="action-btn group"
        :disabled="releaseBlocked"
        @click="$emit('submitForFinalMec')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Confirm submission to MEC for review
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.reopenForEditing">
      <AppButton
        class="action-btn group"
        :disabled="snapshotRendering"
        @click="$emit('reopenForEditing')"
      >
        <FontAwesomeIcon
          :icon="faXmark"
          class="text-lg text-black group-hover:text-white"
        />
        Re-open for editing prior to submission
      </AppButton>
      <span />
    </template>

    <template v-if="showGeneratingMessage">
      <span class="block text-xs italic leading-relaxed">
        {{ generatingMessage }}
      </span>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.approveFinalForCouncil">
      <AppButton
        class="action-btn group"
        @click="$emit('approveFinalForCouncil')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Approve final for Council approval
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.approveFinalAndPublish">
      <AppButton
        class="action-btn group"
        @click="$emit('approveFinalAndPublish')"
      >
        <FontAwesomeIcon
          :icon="faCheck"
          class="text-lg text-black group-hover:text-white"
        />
        Approve final and publish
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.rejectFinal">
      <AppButton
        class="action-btn group"
        @click="$emit('rejectFinal')"
      >
        <FontAwesomeIcon
          :icon="faXmark"
          class="text-lg text-black group-hover:text-white"
        />
        Reject final
      </AppButton>
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.downloadDraft">
      <PlanOverviewDownloadPdfButton
        label="Draft"
        :status="planPdfs?.[PlanWorkflowStep.WorkInProgress3]?.status"
        @download="$emit('downloadDraft')"
        @regenerate="$emit('regenerate')"
      />
      <span />
    </template>

    <template v-if="activeRoleContent.buttons.downloadFinal">
      <PlanOverviewDownloadPdfButton
        label="Final"
        :status="planPdfs?.[PlanWorkflowStep.PublishedAsFinal]?.status"
        @download="$emit('downloadFinal')"
        @regenerate="$emit('regenerate')"
      />
      <span />
    </template>
  </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBellOn } from '@fortawesome/pro-solid-svg-icons';
import { faCheck, faCircleInfo, faFilePen, faPencil, faEye, faXmark } from '@fortawesome/free-solid-svg-icons';

import { AppButton, AppRouterButton, AppTooltip } from '@/components/app';
import { PlanOverviewDownloadPdfButton } from '@/components/plan/overview';

import { computed } from 'vue';

import { usePlanOverviewRole } from '@/composables';
import { useAuthStore } from '@/store';
import { PlanWorkflowStep } from '@/const';

const props = defineProps({
  workflowStep: {
    type: String,
    required: true,
  },
  planPdfs: {
    type: Object,
    default: () => ({}),
  },
});

defineEmits([
  'approve',
  'approveDraftForCouncil',
  'approveFinalAndPublish',
  'approveFinalForCouncil',
  'downloadDraft',
  'downloadFinal',
  'notifyAdminToApprove',
  'notifyAdminToSubmit',
  'regenerate',
  'rejectDraft',
  'rejectFinal',
  'reopenForEditing',
  'submitForApproval',
  'submitForFirstMec',
  'submitForFinalMec',
]);

const authStore = useAuthStore();
const { getActiveRoleContent } = usePlanOverviewRole();

// A forward step may not advance until the snapshot backing its phase has generated. The published-draft
// snapshot is captured on entering WorkInProgress3, so it gates the move out to Council Approval.
const RELEASE_SNAPSHOT_GATES = {
  [PlanWorkflowStep.SubmittedForMecReview]: PlanWorkflowStep.SubmittedForCouncilNoting,
  [PlanWorkflowStep.WorkInProgress3]: PlanWorkflowStep.WorkInProgress3,
  [PlanWorkflowStep.SubmittedToMec]: PlanWorkflowStep.SubmittedForCouncilApproval,
};

const releaseBlockedMessage = 'The submission PDF must finish generating successfully before you can proceed.';
const generatingMessage = 'These unlock once the submission PDF has finished generating.';

const municipalityId = computed(() => authStore.currentMunicipality?.id);
const activeRoleContent = computed(() => getActiveRoleContent(props.workflowStep));

const releaseBlocked = computed(() => {
  const snapshotStep = RELEASE_SNAPSHOT_GATES[props.workflowStep];

  return snapshotStep ? props.planPdfs?.[snapshotStep]?.status !== 'complete' : false;
});

// While a render is in flight both the forward action and re-opening are held back — the forward step needs
// the finished snapshot, and re-opening would delete it mid-render. A stale snapshot will never finish, so it
// stops holding anything back and leaves the step with a way out.
const snapshotRendering = computed(() => {
  const snapshot = props.planPdfs?.[RELEASE_SNAPSHOT_GATES[props.workflowStep]];

  return snapshot?.status === 'pending' && !snapshot.stale;
});

// The note speaks for several actions at once, so it only belongs on the steps that hold back re-opening
// alongside the forward step. Where the forward step is the only thing waiting, its tooltip says as much.
const showGeneratingMessage = computed(
  () => snapshotRendering.value && Boolean(activeRoleContent.value.buttons.reopenForEditing),
);
</script>

<style scoped>
.action-btn {
  @apply w-full !text-black !font-medium border border-editor-btn-border !justify-start !px-3 h-11 hover:bg-primary hover:border-primary hover:!text-white;
}

.action-btn[disabled] {
  @apply !bg-chrome-light !text-chrome !border-chrome-light;
}

.action-btn[disabled] :deep(svg) {
  @apply !text-chrome;
}
</style>
